import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Check, X, ChevronDown, MapPin, Globe, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/lib/context';
import {
  ALL_COUNTRIES,
  getCitiesForCountry,
  searchCountries,
  searchCities,
  getCountryByCode,
} from '@/lib/countries-data';

/**
 * LocationAutocomplete - Global searchable autocomplete for country/city selection
 * 
 * Features:
 * - Complete ISO 3166-1 country list with native names & flags
 * - Major cities for each country
 * - Free-text input with autocomplete
 * - Keyboard navigation (Arrow Up/Down, Enter, Escape)
 * - i18n support via translation keys
 * 
 * @param {string} type - 'country' or 'city'
 * @param {string} value - Current value
 * @param {function} onChange - Callback when value changes
 * @param {string} countryCode - Selected country (for city filtering)
 * @param {string} placeholder - Placeholder text
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disable the input
 * @param {boolean} allowFreeText - Allow custom values not in the list
 */
export const LocationAutocomplete = ({
  type = 'country',
  value = '',
  onChange,
  countryCode,
  placeholder,
  className,
  disabled = false,
  allowFreeText = true,
  'data-testid': testId = 'location-autocomplete'
}) => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const listRef = useRef(null);

  // Get localized country name with fallbacks
  const getCountryLabel = useCallback((code) => {
    // First try i18n translation
    const key = `location.countries.${code}`;
    const translated = t(key);
    if (translated !== key) return translated;
    
    // Fallback to country data
    const country = getCountryByCode(code);
    if (country) {
      // Return native name for matching language, otherwise English
      return country.nativeName !== country.name ? country.nativeName : country.name;
    }
    
    return code;
  }, [t]);

  // Build suggestions based on type and search input
  const suggestions = useMemo(() => {
    if (type === 'country') {
      // Search countries
      const results = searchCountries(inputValue, 50);
      return results.map(c => ({
        value: c.code,
        code: c.code,
        label: getCountryLabel(c.code),
        sublabel: c.name !== getCountryLabel(c.code) ? c.name : null,
        flag: c.flag,
        isRemote: c.code === 'REMOTE'
      }));
    } else {
      // Search cities
      const results = searchCities(inputValue, countryCode, 30);
      return results.map(city => ({
        value: city,
        label: city,
        code: null
      }));
    }
  }, [type, inputValue, countryCode, getCountryLabel]);

  // Check if current input exactly matches a suggestion
  const exactMatch = useMemo(() => {
    const query = inputValue.toLowerCase().trim();
    return suggestions.find(s => 
      s.label.toLowerCase() === query || 
      (s.code && s.code.toLowerCase() === query)
    );
  }, [suggestions, inputValue]);

  // Show custom option when typing non-matching text and allowFreeText is true
  const showCustomOption = useMemo(() => {
    return allowFreeText && inputValue.trim().length > 1 && !exactMatch;
  }, [allowFreeText, inputValue, exactMatch]);

  // All options including custom
  const allOptions = useMemo(() => {
    const options = [...suggestions];
    if (showCustomOption) {
      options.push({ 
        value: inputValue.trim(), 
        label: inputValue.trim(),
        isCustom: true 
      });
    }
    return options;
  }, [suggestions, showCustomOption, inputValue]);

  // Initialize display value from prop value
  useEffect(() => {
    if (type === 'country' && value) {
      const country = getCountryByCode(value);
      if (country) {
        setInputValue(getCountryLabel(country.code));
      } else {
        setInputValue(value);
      }
    } else if (value) {
      setInputValue(value);
    } else {
      setInputValue('');
    }
  }, [value, type, getCountryLabel]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Scroll highlighted item into view
  useEffect(() => {
    if (isOpen && listRef.current && highlightedIndex >= 0) {
      const items = listRef.current.querySelectorAll('[role="option"]');
      if (items[highlightedIndex]) {
        items[highlightedIndex].scrollIntoView({ block: 'nearest' });
      }
    }
  }, [highlightedIndex, isOpen]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        setIsOpen(true);
        e.preventDefault();
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev < allOptions.length - 1 ? prev + 1 : 0
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex(prev => 
          prev > 0 ? prev - 1 : allOptions.length - 1
        );
        break;
      case 'Enter':
        e.preventDefault();
        if (allOptions[highlightedIndex]) {
          handleSelect(allOptions[highlightedIndex]);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
    }
  };

  // Handle selection
  const handleSelect = (option) => {
    setInputValue(option.label);
    // Return code for countries, value for cities/custom
    const returnValue = option.isCustom ? option.value : (option.code || option.value);
    onChange(returnValue);
    setIsOpen(false);
    setHighlightedIndex(0);
    inputRef.current?.blur();
  };

  // Handle input change
  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setIsOpen(true);
    setHighlightedIndex(0);
    
    // For city type with free text, also update parent immediately
    if (type === 'city' && allowFreeText) {
      onChange(newValue);
    }
  };

  // Handle focus
  const handleFocus = () => {
    setIsOpen(true);
    // Select all text on focus for easy replacement
    if (inputRef.current) {
      inputRef.current.select();
    }
  };

  // Clear selection
  const handleClear = (e) => {
    e.stopPropagation();
    setInputValue('');
    onChange('');
    inputRef.current?.focus();
  };

  // Placeholder text
  const placeholderText = placeholder || (type === 'country' 
    ? t('location.searchCountry') 
    : t('location.searchCity'));

  const Icon = type === 'country' ? Globe : MapPin;

  return (
    <div 
      ref={containerRef}
      className={cn('relative', className)}
      data-testid={testId}
    >
      {/* Input field */}
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          placeholder={placeholderText}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background pl-10 pr-16 py-2 text-sm",
            "ring-offset-background placeholder:text-muted-foreground",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50"
          )}
          data-testid={`${testId}-input`}
          autoComplete="off"
          role="combobox"
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-controls={`${testId}-listbox`}
        />
        
        {/* Right icons */}
        <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
          {inputValue && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 hover:bg-muted rounded-sm transition-colors"
              data-testid={`${testId}-clear`}
              aria-label={t('common.clear')}
            >
              <X className="h-4 w-4 text-muted-foreground" />
            </button>
          )}
          <ChevronDown className={cn(
            "h-4 w-4 text-muted-foreground transition-transform",
            isOpen && "rotate-180"
          )} />
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div 
          className="absolute z-50 mt-1 w-full rounded-md border bg-popover shadow-lg animate-in fade-in-0 zoom-in-95"
          data-testid={`${testId}-dropdown`}
        >
          <ul 
            ref={listRef}
            id={`${testId}-listbox`}
            role="listbox"
            className="max-h-60 overflow-auto py-1"
          >
            {allOptions.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground text-center">
                {type === 'country' 
                  ? t('location.noCountryFound') 
                  : t('location.noCityFound')}
              </li>
            ) : (
              allOptions.map((option, index) => (
                <li
                  key={`${option.value}-${index}`}
                  role="option"
                  aria-selected={highlightedIndex === index}
                  onClick={() => handleSelect(option)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 text-sm cursor-pointer transition-colors",
                    highlightedIndex === index && "bg-accent text-accent-foreground",
                    option.isCustom && "border-t border-dashed"
                  )}
                  data-testid={`${testId}-option-${option.value}`}
                >
                  {option.isCustom ? (
                    <>
                      <Search className="h-4 w-4 text-primary shrink-0" />
                      <span className="flex-1 truncate">
                        <span className="font-medium">{t('common.use')}</span>
                        <span className="text-muted-foreground ml-1">"{option.label}"</span>
                      </span>
                    </>
                  ) : (
                    <>
                      {/* Checkmark for selected */}
                      {value === option.value || value === option.code ? (
                        <Check className="h-4 w-4 text-primary shrink-0" />
                      ) : (
                        <div className="w-4 shrink-0" />
                      )}
                      
                      {/* Flag for countries */}
                      {option.flag && (
                        <span className="text-base shrink-0">{option.flag}</span>
                      )}
                      
                      {/* Icon for cities */}
                      {!option.flag && type === 'city' && (
                        <MapPin className="h-4 w-4 text-muted-foreground shrink-0" />
                      )}
                      
                      {/* Label */}
                      <div className="flex-1 min-w-0">
                        <div className="truncate">{option.label}</div>
                        {option.sublabel && (
                          <div className="text-xs text-muted-foreground truncate">
                            {option.sublabel}
                          </div>
                        )}
                      </div>
                      
                      {/* Country code badge */}
                      {option.code && option.code !== option.label && !option.isRemote && (
                        <span className="text-xs text-muted-foreground shrink-0 font-mono">
                          {option.code}
                        </span>
                      )}
                    </>
                  )}
                </li>
              ))
            )}
          </ul>
          
          {/* Hint text */}
          {type === 'country' && inputValue.length === 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t">
              {t('location.typeToSearch')}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default LocationAutocomplete;
